import zxcvbn from 'zxcvbn'
import { AuthErrorCode, CommonErrorCode } from './errors'

export const PASSWORD_MIN_LENGTH = 6
export const PASSWORD_MAX_LENGTH = 255
export const PASSWORD_MIN_SCORE: zxcvbn.ZXCVBNScore = 2

export type PasswordStrengthLevel = 'weak' | 'medium' | 'strong'

export type PasswordPolicyErrorCode =
  | typeof CommonErrorCode.ValidationFailed
  | typeof AuthErrorCode.PasswordTooShort
  | typeof AuthErrorCode.PasswordTooLong
  | typeof AuthErrorCode.WeakPassword

export interface PasswordStrengthAssessment {
  score: zxcvbn.ZXCVBNScore
  level: PasswordStrengthLevel
  isAcceptable: boolean
  feedback: zxcvbn.ZXCVBNFeedback
  guessesLog10: number
  crackTimeDisplay: string
}

export type PasswordPolicyResult =
  | { success: true; assessment: PasswordStrengthAssessment }
  | {
      success: false
      code: PasswordPolicyErrorCode
      message: string
      assessment: PasswordStrengthAssessment
    }

export function passwordUserInputs(inputs: readonly (string | null | undefined)[] = []): string[] {
  return Array.from(
    new Set(
      inputs.map((input) => input?.trim()).filter((input): input is string => Boolean(input)),
    ),
  )
}

export function passwordStrengthLevel(score: zxcvbn.ZXCVBNScore): PasswordStrengthLevel {
  if (score <= 1) return 'weak'
  if (score === 2) return 'medium'
  return 'strong'
}

export function evaluatePasswordStrength(
  password: string,
  userInputs: readonly (string | null | undefined)[] = [],
): PasswordStrengthAssessment {
  const result = zxcvbn(password, passwordUserInputs(userInputs))
  return {
    score: result.score,
    level: passwordStrengthLevel(result.score),
    isAcceptable:
      password.length >= PASSWORD_MIN_LENGTH &&
      password.length <= PASSWORD_MAX_LENGTH &&
      result.score >= PASSWORD_MIN_SCORE,
    feedback: result.feedback,
    guessesLog10: result.guesses_log10,
    crackTimeDisplay: result.crack_times_display.offline_slow_hashing_1e4_per_second.toString(),
  }
}

export function validatePasswordPolicy(
  password: string,
  userInputs: readonly (string | null | undefined)[] = [],
): PasswordPolicyResult {
  const assessment = evaluatePasswordStrength(password, userInputs)
  if (!password) {
    return {
      success: false,
      code: CommonErrorCode.ValidationFailed,
      message: 'Password is required',
      assessment,
    }
  }
  if (password.length < PASSWORD_MIN_LENGTH) {
    return {
      success: false,
      code: AuthErrorCode.PasswordTooShort,
      message: `Password must be at least ${PASSWORD_MIN_LENGTH} characters`,
      assessment,
    }
  }
  if (password.length > PASSWORD_MAX_LENGTH) {
    return {
      success: false,
      code: AuthErrorCode.PasswordTooLong,
      message: `Password must be at most ${PASSWORD_MAX_LENGTH} characters`,
      assessment,
    }
  }
  if (assessment.score < PASSWORD_MIN_SCORE) {
    return {
      success: false,
      code: AuthErrorCode.WeakPassword,
      message: 'Password is too weak',
      assessment,
    }
  }
  return { success: true, assessment }
}

export function passwordMeetsPolicy(
  password: string,
  userInputs: readonly (string | null | undefined)[] = [],
): boolean {
  return validatePasswordPolicy(password, userInputs).success
}
